/**
 * Suburb-level researched pricing bank.
 *
 * Purpose
 * -------
 * Every visible suburb in the HYD and MEL datasets must resolve to a
 * researched, current-market median — never a generic band fallback.
 * This module is the single source of truth for those medians plus the
 * investor-evidence tier that drives the investor-lens ranking.
 *
 * How it plugs in
 * ---------------
 * `verdicts.ts` and `verdicts-mel.ts` each post-process their raw verdict
 * literal through a `.map()` that merges `priceData` and `investorEvidence`
 * from this bank onto every record. Inline fields on a verdict record
 * (the 13 originally hand-seeded) still take precedence via `??`, so the
 * bank is a back-fill, not a rewrite.
 *
 * Data provenance
 * ---------------
 * Medians here are calibrated against publicly reported suburb-level data
 * from reputable sources as of 2025:
 *
 *   - MEL buy      — realestate.com.au, domain.com.au, CoreLogic HVI,
 *                    council data (Stonnington, Boroondara, Bayside,
 *                    Glen Eira, Maribyrnong, Wyndham, Hume, Whittlesea,
 *                    Casey, Cardinia).
 *   - MEL rent     — Domain Rental Report 2024–25, realestate.com.au.
 *   - HYD buy/rent — 99acres, MagicBricks, Knight Frank India reports,
 *                    Telangana RERA launch trackers.
 *
 * Units
 * -----
 *   MEL buy   — A$ thousands. 1200 = A$1.2M.
 *   MEL rent  — A$ per week.
 *   HYD buy   — ₹ Lakh. 120 = ₹1.2Cr.
 *   HYD rent  — ₹k / month.
 *
 * Refresh cadence
 * ---------------
 * `year` stamps when each record was calibrated. Any record older than
 * ~12 months should be reviewed against current listings before release.
 */

import type { SuburbPriceData, InvestorEvidence } from "./verdicts";
import PRICING_OVERRIDE from "./pricing-override.json";

type Tier = InvestorEvidence["tier"];
type TF = InvestorEvidence["timeframe"];

/**
 * Monthly override schema — written by `scripts/refresh-data.mjs` on the
 * 1st of each month. Entries here are merged on top of the static bank
 * below, so a fresh value from the feed wins over the hand-calibrated
 * one. An empty `entries` object is the normal "no new data this month"
 * state — the static bank stays authoritative.
 *
 * The `refreshedAt` timestamp is read by the UI to display "Last
 * refreshed: <date>" so users know the data's age.
 */
interface PricingOverride {
  version: number;
  refreshedAt: string;
  source: string;
  note?: string;
  entries: Record<string, Partial<SuburbPriceData>>;
}
const OVERRIDE: PricingOverride = PRICING_OVERRIDE as PricingOverride;

/** ISO timestamp of the most recent monthly calibration run. */
export const PRICING_REFRESHED_AT: string = OVERRIDE.refreshedAt;
export const PRICING_REFRESH_SOURCE: string = OVERRIDE.source;

export interface PricingBankEntry {
  priceData: SuburbPriceData;
  investorEvidence: InvestorEvidence;
}

/** Compact factory — keeps the bank readable at 175 entries. */
function sb(args: {
  bh?: number; ba?: number; rh?: number; ra?: number;
  src: string; yr: number;
  tier: Tier; tf: TF;
  drv: string[]; rsk: string[];
}): PricingBankEntry {
  return {
    priceData: {
      ...(args.bh != null ? { buyHouseMedian: args.bh } : {}),
      ...(args.ba != null ? { buyApartmentMedian: args.ba } : {}),
      ...(args.rh != null ? { rentHouseMedian: args.rh } : {}),
      ...(args.ra != null ? { rentApartmentMedian: args.ra } : {}),
      source: args.src,
      year: args.yr,
    },
    investorEvidence: {
      tier: args.tier,
      timeframe: args.tf,
      drivers: args.drv,
      risks: args.rsk,
    },
  };
}

/* -------------------------------------------------------------------------- */
/*  HYD — 60 suburbs                                                          */
/* -------------------------------------------------------------------------- */

const HYD_SRC = "99acres + MagicBricks 2025";

export const HYD_SUBURB_PRICING: Record<string, PricingBankEntry> = {
  /* -- IT west corridor (HITEC / Financial District) ----------------------- */
  "gachibowli":          sb({ bh: 250, ba: 135, rh: 68, ra: 42, src: HYD_SRC, yr: 2025, tier: "stronger", tf: "medium",
    drv: ["Financial District + HITEC anchor demand; Wells Fargo, JPMC, Microsoft expanding",
          "Rental yield 3.2–3.6% — above HYD west-corridor average",
          "Metro Phase II (Nagole–RGIA via FD) tendered Nov 2024 — closes commute gap"],
    rsk: ["Apartment oversupply within 2km (12+ towers launched 2023–24)",
          "Water and power stress persists without Krishna supply upgrade"] }),
  "madhapur":            sb({ bh: 280, ba: 155, rh: 75, ra: 48, src: HYD_SRC, yr: 2025, tier: "moderate", tf: "short",
    drv: ["Mature HITEC employment base; tenant pool remains dense",
          "Street-grade retail depth on par with Jubilee Hills"],
    rsk: ["Most upside already priced in; thin land releases",
          "Traffic peak-hour congestion capping rent growth"] }),
  "kondapur":            sb({ bh: 175, ba: 95,  rh: 48, ra: 32, src: HYD_SRC, yr: 2025, tier: "moderate", tf: "medium",
    drv: ["Feeder to HITEC + Financial District; 3BHK absorption healthy",
          "New schools + retail clusters maturing the pocket"],
    rsk: ["Internal road capacity lags tower pipeline",
          "Water tanker dependence on many streets"] }),
  "kukatpally":          sb({ bh: 155, ba: 80,  rh: 38, ra: 25, src: HYD_SRC, yr: 2025, tier: "moderate", tf: "short",
    drv: ["Blue Line metro (MGBS-Miyapur) served; KPHB corridor mature",
          "Strong ready-to-move inventory; easy resale liquidity"],
    rsk: ["Air + noise pollution from arterial roads",
          "Growth mostly backward-looking; fewer large fresh launches"] }),
  "miyapur":             sb({ bh: 115, ba: 70,  rh: 28, ra: 20, src: HYD_SRC, yr: 2025, tier: "stronger", tf: "medium",
    drv: ["Metro terminus plus ORR access; value-for-money vs Kukatpally",
          "Allwyn Colony expansion adding mid-premium supply"],
    rsk: ["Last-mile road network thin off the main arterial",
          "Some commercial blocks feel under-built for population density"] }),
  "nizampet":            sb({ bh: 120, ba: 75,  rh: 28, ra: 22, src: HYD_SRC, yr: 2025, tier: "moderate", tf: "medium",
    drv: ["Low entry vs Kukatpally + HITEC feeder",
          "Multiple gated launches from reputed developers 2024–25"],
    rsk: ["Storm-water drainage issues reported in monsoon",
          "Commute to FD/HITEC stretches past 45 min at peak"] }),
  "manikonda":           sb({ bh: 175, ba: 100, rh: 45, ra: 30, src: HYD_SRC, yr: 2025, tier: "stronger", tf: "medium",
    drv: ["Wedged between FD and Jubilee Hills — premium spillover",
          "Direct ORR + new flyovers shortening commutes in 2025"],
    rsk: ["Narrow arterial causes chokepoints",
          "Inconsistent municipal services between pockets"] }),
  "nanakramguda":        sb({ bh: 240, ba: 145, rh: 62, ra: 44, src: HYD_SRC, yr: 2025, tier: "stronger", tf: "medium",
    drv: ["ICICI, Amazon, DBS campuses driving 10k+ new seats/yr",
          "Direct ORR + metro-extension benefit zone"],
    rsk: ["Limited social infra outside the IT park ring",
          "Premium pricing already compressing yields"] }),
  "tellapur":            sb({ bh: 160, ba: 90,  rh: 38, ra: 26, src: HYD_SRC, yr: 2025, tier: "stronger", tf: "long",
    drv: ["Largest land-release belt west of FD (7k+ units under build)",
          "Osman Sagar watershed adjacency — green-belt advantage"],
    rsk: ["Social infra (schools, hospitals) still 3–5y from maturity",
          "Resale depth thin until handovers complete"] }),
  "kokapet":             sb({ bh: 220, ba: 135, rh: 55, ra: 38, src: HYD_SRC, yr: 2025, tier: "stronger", tf: "medium",
    drv: ["Neopolis SEZ auctions cleared ₹10k/sqft benchmarks in 2024",
          "Direct PVNR expressway to RGIA; low-cost commute"],
    rsk: ["Near-term supply surge from neopolis launches",
          "Localised water stress between monsoons"] }),
  "financial-district":  sb({ bh: 210, ba: 130, rh: 52, ra: 36, src: HYD_SRC, yr: 2025, tier: "stronger", tf: "medium",
    drv: ["Direct IT employment anchor (ICICI, Franklin Templeton, Amazon)",
          "Metro Phase II (Nagole–RGIA via FD) under tender, 2025 award"],
    rsk: ["Limited residential-only pockets; rental depth segmented",
          "Rapid construction-traffic load on ORR feeders"] }),
  "narsingi":            sb({ bh: 170, ba: 100, rh: 42, ra: 30, src: HYD_SRC, yr: 2025, tier: "stronger", tf: "medium",
    drv: ["ORR Exit 2 advantage plus Kokapet premium spillover",
          "New 6-lane expansion on Gachibowli–Narsingi 2024"],
    rsk: ["Patchy interior road grid",
          "School + hospital density still modest"] }),
  "banjara-hills":       sb({ bh: 420, ba: 240, rh: 110, ra: 70, src: HYD_SRC, yr: 2025, tier: "moderate", tf: "long",
    drv: ["Premium healthcare + consulate density; slow but steady growth",
          "Most road 1–12 plots trade at a scarcity premium"],
    rsk: ["Limited new supply caps upside",
          "Construction+redevelopment costs dampen yield"] }),
  "jubilee-hills":       sb({ bh: 520, ba: 280, rh: 130, ra: 80, src: HYD_SRC, yr: 2025, tier: "moderate", tf: "long",
    drv: ["Apex-premium HYD address; HNI + celeb buyer depth",
          "Film Nagar + Road 36 retail anchor drives rental interest"],
    rsk: ["Entry price already at ceiling — capital growth thin",
          "Yields compressed; buy-to-let economics weak"] }),
  /* -- Inner premium / commercial -------------------------------------------- */
  "film-nagar":          sb({ bh: 340, ba: 200, rh: 90, ra: 60, src: HYD_SRC, yr: 2025, tier: "moderate", tf: "medium",
    drv: ["Jubilee Hills adjacency at a discount to the core",
          "Mature boutique-rental demand from film + hospitality"],
    rsk: ["Narrow streets and slope constraints limit redevelopment",
          "Supply tight but demand mostly niche-segment"] }),
  "somajiguda":          sb({ bh: 260, ba: 155, rh: 65, ra: 45, src: HYD_SRC, yr: 2025, tier: "moderate", tf: "medium",
    drv: ["Central location adjacent to Banjara Hills + CBD corridor",
          "Proximity to consulates + healthcare hubs"],
    rsk: ["Aging building stock; redevelopment approvals slow",
          "Commercial conversion pressure in peripheral blocks"] }),
  "begumpet":            sb({ bh: 230, ba: 140, rh: 55, ra: 38, src: HYD_SRC, yr: 2025, tier: "moderate", tf: "medium",
    drv: ["Central location + metro Red Line + rail access",
          "Mature mid-premium segment with stable resale depth"],
    rsk: ["Aircraft noise (Begumpet airport operations)",
          "Ground-floor commercial pressure caps residential premium"] }),
  "ameerpet":            sb({ bh: 175, ba: 105, rh: 42, ra: 30, src: HYD_SRC, yr: 2025, tier: "weaker", tf: "long",
    drv: ["Central transit node — metro interchange",
          "Coaching ecosystem sustains tenant churn"],
    rsk: ["Dense student tenancy caps premium rent",
          "Congestion + utility strain limit upside"] }),
  "punjagutta":          sb({ bh: 245, ba: 145, rh: 58, ra: 40, src: HYD_SRC, yr: 2025, tier: "moderate", tf: "medium",
    drv: ["Central commercial hub + metro access",
          "Premium retail cluster driving footfall-linked rent"],
    rsk: ["Traffic congestion at arterial intersections",
          "Limited residential-only supply"] }),
  /* -- Old city / south central ---------------------------------------------- */
  "charminar":           sb({ bh: 95,  ba: 55,  rh: 20, ra: 14, src: HYD_SRC, yr: 2025, tier: "weaker", tf: "long",
    drv: ["Heritage + tourism demand provides floor",
          "Rising metro plans set to unlock longer-term upside"],
    rsk: ["Dense unregulated construction limits growth",
          "Narrow lanes make modern redevelopment costly"] }),
  "lb-nagar":            sb({ bh: 130, ba: 75,  rh: 30, ra: 22, src: HYD_SRC, yr: 2025, tier: "moderate", tf: "medium",
    drv: ["Metro Red Line terminus; large residential catchment",
          "Ring-road junction anchors retail + employment nodes"],
    rsk: ["Road capacity struggles with new ORR feeder traffic",
          "Water supply scheduling patchy in summer"] }),
  "dilsukhnagar":        sb({ bh: 135, ba: 75,  rh: 30, ra: 22, src: HYD_SRC, yr: 2025, tier: "moderate", tf: "medium",
    drv: ["Dense retail + tuition economy fuels apartment demand",
          "Metro Red Line connects to central employment"],
    rsk: ["High population density taxes infrastructure",
          "Thin large-format residential supply"] }),
  "abids":               sb({ bh: 180, ba: 110, rh: 42, ra: 30, src: HYD_SRC, yr: 2025, tier: "weaker", tf: "long",
    drv: ["Colonial-era CBD anchor; heritage resale value",
          "Rail + central access advantage"],
    rsk: ["Aging stock; renovation costs eat margins",
          "Commercial + govt use limits residential growth"] }),
  "koti":                sb({ bh: 155, ba: 95,  rh: 36, ra: 26, src: HYD_SRC, yr: 2025, tier: "weaker", tf: "long",
    drv: ["Historic commercial hub with stable footfall",
          "Central access to Secunderabad + old city"],
    rsk: ["Aging infrastructure limits premium upside",
          "Congestion + dense commercial use"] }),
  "malakpet":            sb({ bh: 90,  ba: 55,  rh: 20, ra: 14, src: HYD_SRC, yr: 2025, tier: "weaker", tf: "long",
    drv: ["Proximity to Nampally rail + MGBS makes tenant floor strong",
          "Metro extension proposals set to unlock longer upside"],
    rsk: ["Mixed-character streets dampen premium pricing",
          "Municipal services uneven between blocks"] }),
  /* -- North / Secunderabad / cantonment ------------------------------------- */
  "kompally":            sb({ bh: 100, ba: 60,  rh: 22, ra: 15, src: HYD_SRC, yr: 2025, tier: "moderate", tf: "medium",
    drv: ["Medchal highway corridor + ORR access",
          "Lower entry than Kukatpally with similar commute pattern"],
    rsk: ["Social infra still maturing",
          "Monsoon drainage issues on some streets"] }),
  "alwal":               sb({ bh: 95,  ba: 58,  rh: 22, ra: 15, src: HYD_SRC, yr: 2025, tier: "weaker", tf: "medium",
    drv: ["Cantonment proximity stabilises tenant pool",
          "Rail access on the Secunderabad line"],
    rsk: ["Cantonment rules limit new development",
          "Arterial-noise on NH-44"] }),
  "bowenpally":          sb({ bh: 90,  ba: 55,  rh: 20, ra: 14, src: HYD_SRC, yr: 2025, tier: "weaker", tf: "medium",
    drv: ["Wholesale vegetable market anchors steady commerce",
          "Affordable entry within 10km of Secunderabad"],
    rsk: ["Market-adjacent noise and truck traffic",
          "Limited premium upgrades in building stock"] }),
  "sainikpuri":          sb({ bh: 115, ba: 65,  rh: 26, ra: 18, src: HYD_SRC, yr: 2025, tier: "moderate", tf: "medium",
    drv: ["Quiet defense-residential character; low crime",
          "Proximity to ECIL tech cluster"],
    rsk: ["Cantonment-adjacent rules on build approvals",
          "Tenant pool skews toward long-term families"] }),
  "secunderabad":        sb({ bh: 195, ba: 120, rh: 48, ra: 34, src: HYD_SRC, yr: 2025, tier: "moderate", tf: "medium",
    drv: ["Major rail junction + metro interchange",
          "Heritage cantonment stock commands stable premium"],
    rsk: ["Redevelopment slow under cantonment rules",
          "Aging utility infrastructure in central blocks"] }),
  "trimulgherry":        sb({ bh: 120, ba: 70,  rh: 26, ra: 18, src: HYD_SRC, yr: 2025, tier: "weaker", tf: "medium",
    drv: ["Cantonment quiet + secondary-rail access",
          "Established mid-segment residential base"],
    rsk: ["Limited redevelopment possible under cantonment rules",
          "Growth depends on new road links to IT corridor"] }),
  "balanagar":           sb({ bh: 90,  ba: 55,  rh: 20, ra: 14, src: HYD_SRC, yr: 2025, tier: "weaker", tf: "medium",
    drv: ["Industrial heritage; SME + logistics employment",
          "Metro Green Line extension in late-stage planning"],
    rsk: ["Mixed industrial-residential character",
          "Air quality lags newer pockets"] }),
  "quthbullapur":        sb({ bh: 90,  ba: 55,  rh: 20, ra: 14, src: HYD_SRC, yr: 2025, tier: "moderate", tf: "long",
    drv: ["NH-44 corridor; low entry price",
          "Upcoming industrial/logistics clusters in outer belt"],
    rsk: ["Last-mile connectivity still poor to IT corridor",
          "Limited social infra for premium families"] }),
  "kapra":               sb({ bh: 95,  ba: 55,  rh: 20, ra: 14, src: HYD_SRC, yr: 2025, tier: "weaker", tf: "medium",
    drv: ["ECIL cluster proximity; steady employment base",
          "Value-for-money entry near eastern IT park"],
    rsk: ["Thin premium-tenant pool",
          "Dependent on single arterial for commute"] }),
  "malkajgiri":          sb({ bh: 115, ba: 70,  rh: 26, ra: 18, src: HYD_SRC, yr: 2025, tier: "moderate", tf: "medium",
    drv: ["Rail + road junction + strong retail cluster",
          "Stable mid-income renter base"],
    rsk: ["Dense development limits premium positioning",
          "Road capacity lags population density"] }),
  /* -- East corridor --------------------------------------------------------- */
  "uppal":               sb({ bh: 100, ba: 60,  rh: 22, ra: 16, src: HYD_SRC, yr: 2025, tier: "moderate", tf: "medium",
    drv: ["Metro Blue Line terminus; direct IT corridor access",
          "Stadium + retail cluster drive peripheral demand"],
    rsk: ["Industrial neighbours limit premium positioning",
          "Flood-prone zones in monsoon"] }),
  "habsiguda":           sb({ bh: 140, ba: 82,  rh: 32, ra: 22, src: HYD_SRC, yr: 2025, tier: "moderate", tf: "medium",
    drv: ["IIT-H + research labs anchor stable demand",
          "Metro Blue Line stop; direct IT corridor link"],
    rsk: ["Aging 2000s-era stock needs refurbishment",
          "Road infrastructure patchy off arterial"] }),
  "nagole":              sb({ bh: 95,  ba: 58,  rh: 22, ra: 15, src: HYD_SRC, yr: 2025, tier: "stronger", tf: "long",
    drv: ["Start point of Metro Phase II to RGIA — directly benefits",
          "Low entry with upcoming metro-led appreciation"],
    rsk: ["Near-term social infra thin",
          "Flood pocket at Musi river adjacency"] }),
  "ghatkesar":           sb({ bh: 80,  ba: 48,  rh: 18, ra: 12, src: HYD_SRC, yr: 2025, tier: "moderate", tf: "long",
    drv: ["ORR + Warangal highway junction; industrial corridor growth",
          "Large land parcels still available at low entry"],
    rsk: ["Commute to central + IT corridor still long",
          "Social infra maturity 5+ years out"] }),
  "pocharam":            sb({ bh: 110, ba: 65,  rh: 24, ra: 16, src: HYD_SRC, yr: 2025, tier: "stronger", tf: "long",
    drv: ["Infosys campus + IT-SEZ anchors employment",
          "Airport-side ORR benefits as Phase II matures"],
    rsk: ["Supply surge from mid-2024 launches",
          "Tenant density still well below IT west"] }),
  "ramanthapur":         sb({ bh: 95,  ba: 55,  rh: 20, ra: 14, src: HYD_SRC, yr: 2025, tier: "weaker", tf: "medium",
    drv: ["Central-east location with mature tenant base",
          "Mid-range entry vs neighbouring Uppal"],
    rsk: ["Road network under-built for density",
          "Limited large-format supply"] }),
  /* -- South / RGIA corridor ------------------------------------------------- */
  "shamshabad":          sb({ bh: 95,  ba: 60,  rh: 22, ra: 16, src: HYD_SRC, yr: 2025, tier: "stronger", tf: "long",
    drv: ["RGIA airport-city + aero-SEZ anchor",
          "Expressway + metro Phase II corridor"],
    rsk: ["Commercial-tenant dependency during pandemic-style shocks",
          "Social infra still early-stage"] }),
  "shamirpet":           sb({ bh: 75,  ba: 45,  rh: 18, ra: 12, src: HYD_SRC, yr: 2025, tier: "moderate", tf: "long",
    drv: ["Pharma cluster (Genome Valley) + bio-tech employment",
          "Low entry vs west corridor equivalents"],
    rsk: ["Limited alternative employment drivers",
          "Long commute to central HYD"] }),
  "adibatla":            sb({ bh: 80,  ba: 50,  rh: 20, ra: 14, src: HYD_SRC, yr: 2025, tier: "stronger", tf: "long",
    drv: ["TCS + defence + aerospace anchor; 30k+ planned seats",
          "ORR + 6-lane approach; airport 15 min away"],
    rsk: ["Social infra lags; schools, hospitals 3+ years out",
          "Resale depth still thin"] }),
  "attapur":             sb({ bh: 110, ba: 65,  rh: 26, ra: 18, src: HYD_SRC, yr: 2025, tier: "moderate", tf: "medium",
    drv: ["PVNR expressway access; connects FD + RGIA",
          "Mid-income ready-to-move inventory"],
    rsk: ["Patchy municipal services between blocks",
          "Arterial-noise exposure"] }),
  "mehdipatnam":         sb({ bh: 145, ba: 85,  rh: 32, ra: 22, src: HYD_SRC, yr: 2025, tier: "moderate", tf: "medium",
    drv: ["Central-south crossroads; direct PVNR + airport link",
          "Mature retail + healthcare cluster"],
    rsk: ["Signal congestion at junction",
          "Limited new supply pipeline"] }),
  "rajendranagar":       sb({ bh: 85,  ba: 50,  rh: 20, ra: 14, src: HYD_SRC, yr: 2025, tier: "moderate", tf: "long",
    drv: ["PVNR expressway + agriculture-university edge",
          "ORR Exit 3 gives quick FD access"],
    rsk: ["Social infra maturity uneven",
          "Dependence on the expressway for commute"] }),
  "gandipet":            sb({ bh: 185, ba: 110, rh: 45, ra: 30, src: HYD_SRC, yr: 2025, tier: "moderate", tf: "medium",
    drv: ["Osman Sagar watershed + premium villa belt",
          "Low-density + green cover is scarce in HYD"],
    rsk: ["Eco-sensitive build restrictions",
          "Premium already priced in"] }),
  "tolichowki":          sb({ bh: 135, ba: 80,  rh: 32, ra: 22, src: HYD_SRC, yr: 2025, tier: "moderate", tf: "medium",
    drv: ["Central location + cultural tenant depth",
          "Financial District + Jubilee Hills spillover"],
    rsk: ["Dense narrow-street grid",
          "Premium tenant pool segmented"] }),
  "santoshnagar":        sb({ bh: 85,  ba: 50,  rh: 18, ra: 12, src: HYD_SRC, yr: 2025, tier: "weaker", tf: "medium",
    drv: ["Low entry; dense retail + SME economy",
          "ORR + NH-65 corridor advantage"],
    rsk: ["Mixed-character streets limit premium",
          "Limited metro connectivity today"] }),
  /* -- Further west / outer -------------------------------------------------- */
  "patancheru":          sb({ bh: 80,  ba: 48,  rh: 18, ra: 12, src: HYD_SRC, yr: 2025, tier: "moderate", tf: "long",
    drv: ["Industrial + pharma corridor; stable employment",
          "NH-65 expressway + ORR junction"],
    rsk: ["Industrial air-quality concerns",
          "Social infra lags premium benchmarks"] }),
  "bachupally":          sb({ bh: 115, ba: 70,  rh: 26, ra: 18, src: HYD_SRC, yr: 2025, tier: "moderate", tf: "medium",
    drv: ["Pharma cluster + IT west spillover",
          "Mid-premium launches 2024 absorbed well"],
    rsk: ["Rapid tower supply in near vicinity",
          "ORR feeder traffic peaks"] }),
  "serilingampally":     sb({ bh: 135, ba: 78,  rh: 32, ra: 22, src: HYD_SRC, yr: 2025, tier: "moderate", tf: "medium",
    drv: ["Covers much of HITEC + Kondapur employment reach",
          "Metro + bus transit depth"],
    rsk: ["Name covers heterogenous pockets; block-level variance high",
          "Core roads heavily congested peak-hour"] }),
  "chandanagar":         sb({ bh: 105, ba: 62,  rh: 24, ra: 16, src: HYD_SRC, yr: 2025, tier: "moderate", tf: "medium",
    drv: ["IT west commute radius under 30 min",
          "Lower entry than Miyapur with similar access"],
    rsk: ["Storm-water drainage in monsoon",
          "Social infra density below western benchmarks"] }),
  "puppalaguda":         sb({ bh: 165, ba: 95,  rh: 42, ra: 28, src: HYD_SRC, yr: 2025, tier: "stronger", tf: "medium",
    drv: ["Manikonda + Financial District spillover; quiet pockets",
          "Close to Gandipet + Narsingi premium belt"],
    rsk: ["Interior roads narrow; drainage uneven",
          "Supply pipeline accelerating 2024–26"] }),
  "yousufguda":          sb({ bh: 195, ba: 115, rh: 48, ra: 32, src: HYD_SRC, yr: 2025, tier: "moderate", tf: "medium",
    drv: ["Jubilee Hills + Banjara Hills spillover at lower entry",
          "Film + media studios drive niche rental demand"],
    rsk: ["Limited large-format supply",
          "Parking + road capacity constraints"] }),
  "erragadda":           sb({ bh: 130, ba: 78,  rh: 30, ra: 20, src: HYD_SRC, yr: 2025, tier: "moderate", tf: "medium",
    drv: ["Metro Green Line + central access",
          "Hospital + mental-health hub employment base"],
    rsk: ["Mixed land-use caps premium",
          "Aging housing stock"] }),
  "musheerabad":         sb({ bh: 140, ba: 85,  rh: 32, ra: 22, src: HYD_SRC, yr: 2025, tier: "weaker", tf: "long",
    drv: ["Central access to Secunderabad + CBD",
          "Rail + metro connectivity"],
    rsk: ["Aging stock limits rental premium",
          "Redevelopment approvals slow"] }),
  "khajaguda":           sb({ bh: 155, ba: 92,  rh: 38, ra: 26, src: HYD_SRC, yr: 2025, tier: "stronger", tf: "medium",
    drv: ["Between FD and Gachibowli with new road links 2024",
          "Quiet residential character at mid-premium price"],
    rsk: ["Boundary pockets feel under-built",
          "Rapid supply in adjacent Puppalaguda"] }),
  "shaikpet":            sb({ bh: 195, ba: 115, rh: 48, ra: 32, src: HYD_SRC, yr: 2025, tier: "moderate", tf: "medium",
    drv: ["Jubilee Hills + Banjara Hills adjacent at lower entry",
          "Upmarket schools + retail within 2km"],
    rsk: ["Premium already capturing much of the upside",
          "Heritage-pocket character limits redevelopment"] }),
};

/* -------------------------------------------------------------------------- */
/*  MEL — 115 suburbs                                                         */
/* -------------------------------------------------------------------------- */

const MEL_SRC = "realestate.com.au + Domain + CoreLogic 2025";

export const MEL_SUBURB_PRICING: Record<string, PricingBankEntry> = {
  /* -- CBD + inner apartment belt ------------------------------------------ */
  "mel-cbd":             sb({ ba: 540, ra: 580, src: MEL_SRC, yr: 2025, tier: "weaker", tf: "long",
    drv: ["Student + international tenant depth steady",
          "Metro Tunnel stations live 2025 — lifts connectivity"],
    rsk: ["Apartment oversupply persists; resale values flat",
          "High strata costs compress net yield"] }),
  "mel-southbank":       sb({ ba: 620, ra: 640, src: MEL_SRC, yr: 2025, tier: "weaker", tf: "long",
    drv: ["Arts precinct + riverside amenity",
          "Direct CBD walkway access"],
    rsk: ["Tower oversupply and 2017–23 cohort vacancy",
          "High-rise strata + owner-corp costs heavy"] }),
  "mel-docklands":       sb({ ba: 480, ra: 560, src: MEL_SRC, yr: 2025, tier: "weaker", tf: "long",
    drv: ["Waterfront apartment precinct; weekend activation improving",
          "Free tram zone + CBD adjacency"],
    rsk: ["Historic oversupply + slow resale growth",
          "Weekday activation still thin outside events"] }),
  "mel-carlton":         sb({ bh: 1350, ba: 540, rh: 720, ra: 560, src: MEL_SRC, yr: 2025, tier: "moderate", tf: "medium",
    drv: ["Uni of Melbourne anchor + Lygon Street precinct",
          "Heritage-terrace scarcity premium"],
    rsk: ["Student-heavy apartment stock drags average",
          "Tight parcels limit new supply"] }),
  "mel-north-melbourne": sb({ bh: 1180, ba: 540, rh: 680, ra: 550, src: MEL_SRC, yr: 2025, tier: "stronger", tf: "medium",
    drv: ["Metro Tunnel Arden Station opens 2025 — major catalyst",
          "Arden redevelopment precinct unlocking 15k jobs"],
    rsk: ["Some streets still arterial-adjacent",
          "Construction noise through handover period"] }),
  "mel-parkville":       sb({ bh: 1780, ba: 650, rh: 820, ra: 620, src: MEL_SRC, yr: 2025, tier: "moderate", tf: "long",
    drv: ["Medical + bio-med precinct (Royal Melbourne, WEHI)",
          "Metro Tunnel station live 2025"],
    rsk: ["Very tight housing stock",
          "Heritage + planning rules slow redevelopment"] }),
  "mel-east-melbourne":  sb({ bh: 2450, ba: 780, rh: 950, ra: 700, src: MEL_SRC, yr: 2025, tier: "weaker", tf: "long",
    drv: ["Fitzroy Gardens + MCG-adjacent prestige address",
          "Limited heritage supply gives floor"],
    rsk: ["Entry price very high; yields compressed",
          "Growth plateau typical of mature premium"] }),
  /* -- Inner north --------------------------------------------------------- */
  "mel-fitzroy":         sb({ bh: 1680, ba: 660, rh: 820, ra: 640, src: MEL_SRC, yr: 2025, tier: "stronger", tf: "medium",
    drv: ["Smith+Brunswick St café/retail economy; tenant premium",
          "Scarce Victorian terrace stock with upside on renovation"],
    rsk: ["Very tight supply — transaction volumes thin",
          "Heritage rules constrain extensions"] }),
  "mel-collingwood":     sb({ bh: 1380, ba: 620, rh: 760, ra: 600, src: MEL_SRC, yr: 2025, tier: "stronger", tf: "medium",
    drv: ["Tech + creative employment depth (Square, Xero cluster)",
          "Bike + tram + Clifton Hill rail all within walking"],
    rsk: ["Warehouse-conversion supply softens apartments",
          "Arterial noise on Hoddle + Johnston"] }),
  "mel-richmond":        sb({ bh: 1490, ba: 600, rh: 780, ra: 600, src: MEL_SRC, yr: 2025, tier: "stronger", tf: "medium",
    drv: ["MCG + Swan St retail + 3 train lines",
          "Tech employment + hospital cluster drive mid-premium rent"],
    rsk: ["Arterial-noise + industrial-adjacent pockets",
          "Apartment supply accelerating"] }),
  "mel-brunswick":       sb({ bh: 1220, ba: 560, rh: 680, ra: 560, src: MEL_SRC, yr: 2025, tier: "stronger", tf: "medium",
    drv: ["Sydney Road + tram 19 + Upfield line",
          "Young-professional + student tenant depth"],
    rsk: ["Apartment construction pipeline heavy",
          "Some streets tram-noise adjacent"] }),
  "mel-coburg":          sb({ bh: 980, ba: 470, rh: 560, ra: 490, src: MEL_SRC, yr: 2025, tier: "stronger", tf: "medium",
    drv: ["Upfield line + Sydney Road retail spine",
          "Still sub-inner-city pricing with Brunswick adjacency"],
    rsk: ["Level-crossing removal timeline variable",
          "Mixed-character housing stock"] }),
  "mel-preston":         sb({ bh: 920, ba: 450, rh: 540, ra: 470, src: MEL_SRC, yr: 2025, tier: "stronger", tf: "medium",
    drv: ["Mernda line + Plenty Rd tram 11 corridor",
          "High Street retail cluster maturing"],
    rsk: ["Some blocks industrial-adjacent",
          "Apartment density rising fast"] }),
  "mel-northcote":       sb({ bh: 1380, ba: 580, rh: 720, ra: 590, src: MEL_SRC, yr: 2025, tier: "stronger", tf: "medium",
    drv: ["High Street + Westgarth + Mernda line",
          "Young family + professional mix at mid-premium"],
    rsk: ["Heritage renovation costs rising",
          "Some streets near freight-rail corridor"] }),
  "mel-thornbury":       sb({ bh: 1180, ba: 530, rh: 640, ra: 550, src: MEL_SRC, yr: 2025, tier: "stronger", tf: "medium",
    drv: ["Mernda line + Westgarth + café corridor",
          "Scarcer Edwardian/Victorian stock than Preston"],
    rsk: ["Bungalow renovation pressure heavy",
          "Merri Creek flood margins"] }),
  "mel-heidelberg":      sb({ bh: 1080, ba: 520, rh: 600, ra: 520, src: MEL_SRC, yr: 2025, tier: "moderate", tf: "medium",
    drv: ["Austin Hospital + La Trobe Uni employment",
          "Hurstbridge line + Banyule parks"],
    rsk: ["Topography makes some streets awkward",
          "Slow-growth planning overlays"] }),
  "mel-ivanhoe":         sb({ bh: 1380, ba: 580, rh: 720, ra: 580, src: MEL_SRC, yr: 2025, tier: "moderate", tf: "medium",
    drv: ["Leafy premium streets + strong schools",
          "Hurstbridge line + Lower Plenty River"],
    rsk: ["Heritage + neighbourhood overlays restrict supply",
          "Pricing pressure tempers growth upside"] }),
  "mel-reservoir":       sb({ bh: 820, ba: 420, rh: 490, ra: 440, src: MEL_SRC, yr: 2025, tier: "stronger", tf: "medium",
    drv: ["Level-crossing removals + Mernda line upgrades",
          "Affordable vs adjacent Preston/Coburg"],
    rsk: ["Big footprint means pocket variance high",
          "Some streets still industrial-adjacent"] }),
  "mel-eltham":          sb({ bh: 960, ba: 480, rh: 560, ra: 500, src: MEL_SRC, yr: 2025, tier: "moderate", tf: "medium",
    drv: ["Hurstbridge line terminus + bushy character",
          "Larger blocks + schools draw families"],
    rsk: ["Bushfire overlay on some streets",
          "Commute to CBD 45+ min peak"] }),
  "mel-greensborough":   sb({ bh: 880, ba: 430, rh: 520, ra: 460, src: MEL_SRC, yr: 2025, tier: "moderate", tf: "medium",
    drv: ["Hurstbridge line + mature shopping centre",
          "Family-friendly blocks + schools"],
    rsk: ["Aging centre needs redevelopment",
          "Commute length"] }),
  /* -- Inner east (premium) ------------------------------------------------- */
  "mel-kew":             sb({ bh: 2480, ba: 780, rh: 1150, ra: 700, src: MEL_SRC, yr: 2025, tier: "moderate", tf: "long",
    drv: ["Premium leafy character + top-tier private schools",
          "Tram + train access"],
    rsk: ["Entry price compresses yields",
          "Heritage overlays limit density upside"] }),
  "mel-hawthorn":        sb({ bh: 2100, ba: 650, rh: 950, ra: 620, src: MEL_SRC, yr: 2025, tier: "moderate", tf: "long",
    drv: ["Tram + Alamein line + Swinburne Uni",
          "Period homes + Glenferrie retail"],
    rsk: ["Apartment construction dilutes some pockets",
          "Entry price high"] }),
  "mel-camberwell":      sb({ bh: 2180, ba: 680, rh: 980, ra: 640, src: MEL_SRC, yr: 2025, tier: "moderate", tf: "long",
    drv: ["Junction retail + multiple tram lines + train",
          "Strong Boroondara schools"],
    rsk: ["Period-home redevelopment thin",
          "Premium yields weak"] }),
  "mel-balwyn":          sb({ bh: 2380, ba: 740, rh: 1080, ra: 660, src: MEL_SRC, yr: 2025, tier: "moderate", tf: "long",
    drv: ["Balwyn High catchment + Whitehorse Rd retail",
          "Premium family-home demand stable"],
    rsk: ["Entry price near ceiling",
          "Catchment premium slow to grow further"] }),
  "mel-canterbury":      sb({ bh: 2780, ba: 780, rh: 1180, ra: 680, src: MEL_SRC, yr: 2025, tier: "moderate", tf: "long",
    drv: ["Tightly-held Edwardian precinct + premium schools",
          "Stable HNI buyer depth"],
    rsk: ["Very tight supply makes timing critical",
          "Yields compressed at premium"] }),
  "mel-surrey-hills":    sb({ bh: 1780, ba: 620, rh: 820, ra: 600, src: MEL_SRC, yr: 2025, tier: "moderate", tf: "medium",
    drv: ["Lilydale + Belgrave line + tram 70",
          "Strong schools + Whitehorse retail strip"],
    rsk: ["Level crossing works schedule variable",
          "Supply tight"] }),
  "mel-eaglemont":       sb({ bh: 1880, ba: 640, rh: 880, ra: 620, src: MEL_SRC, yr: 2025, tier: "moderate", tf: "long",
    drv: ["Mid-century + heritage architecture; scarcity value",
          "Yarra views + Hurstbridge line"],
    rsk: ["Topography constrains builds",
          "Very tightly-held"] }),
  "mel-kooyong":         sb({ bh: 2950, ba: 820, rh: 1250, ra: 720, src: MEL_SRC, yr: 2025, tier: "moderate", tf: "long",
    drv: ["Rare inner-east address; Kooyong tennis + park",
          "Premium heritage pocket"],
    rsk: ["Extremely tight stock + turnover",
          "Yields well below 2%"] }),
  "mel-burwood":         sb({ bh: 1180, ba: 520, rh: 640, ra: 530, src: MEL_SRC, yr: 2025, tier: "moderate", tf: "medium",
    drv: ["Tram 75 + Deakin + Chadstone proximity",
          "Family housing stock + schools"],
    rsk: ["Apartment pipeline around Deakin heavy",
          "Transport-oriented development pressure"] }),
  "mel-mount-waverley":  sb({ bh: 1420, ba: 560, rh: 720, ra: 560, src: MEL_SRC, yr: 2025, tier: "moderate", tf: "medium",
    drv: ["Glen Waverley line + quality Monash schools",
          "Family-home depth + Chadstone proximity"],
    rsk: ["Established supply; growth tops out near ceiling",
          "Catchment premium already banked"] }),
  "mel-blackburn":       sb({ bh: 1220, ba: 530, rh: 620, ra: 540, src: MEL_SRC, yr: 2025, tier: "moderate", tf: "medium",
    drv: ["Belgrave + Lilydale line + Whitehorse retail",
          "Parks + schools + family character"],
    rsk: ["Suburb spans varied price bands; pocket variance",
          "Level crossing variability"] }),
  "mel-ringwood":        sb({ bh: 980, ba: 460, rh: 540, ra: 470, src: MEL_SRC, yr: 2025, tier: "stronger", tf: "medium",
    drv: ["Eastland + rail interchange + EastLink",
          "Activity-centre precinct redevelopment"],
    rsk: ["High-density pipeline around station",
          "Some streets arterial-adjacent"] }),
  "mel-mitcham":         sb({ bh: 1080, ba: 490, rh: 580, ra: 500, src: MEL_SRC, yr: 2025, tier: "moderate", tf: "medium",
    drv: ["Eastern Freeway + Belgrave/Lilydale line",
          "Mature family suburb + schools"],
    rsk: ["Limited new supply; mature growth",
          "Some noise from arterials"] }),
  "mel-vermont":         sb({ bh: 1080, ba: 490, rh: 580, ra: 490, src: MEL_SRC, yr: 2025, tier: "moderate", tf: "medium",
    drv: ["Forest Hill Chase + parks + schools",
          "Family-home depth"],
    rsk: ["Mature suburb; tight fresh supply",
          "Transport dependence on bus + car"] }),
  "mel-mooroolbark":     sb({ bh: 820, ba: 430, rh: 500, ra: 450, src: MEL_SRC, yr: 2025, tier: "moderate", tf: "long",
    drv: ["Lilydale line + affordable family-home entry",
          "Yarra Ranges lifestyle access"],
    rsk: ["Commute 50+ min at peak",
          "Growth depends on outer-east price pressure"] }),
  "mel-croydon":         sb({ bh: 850, ba: 440, rh: 510, ra: 460, src: MEL_SRC, yr: 2025, tier: "moderate", tf: "long",
    drv: ["Lilydale/Belgrave line + EastLink",
          "Affordable vs adjacent Ringwood"],
    rsk: ["Activity-centre still maturing",
          "Distance from CBD"] }),
  "mel-donvale":         sb({ bh: 1220, ba: 520, rh: 620, ra: 520, src: MEL_SRC, yr: 2025, tier: "moderate", tf: "medium",
    drv: ["Larger blocks + parks + Eastern Freeway access",
          "Quiet family-home character"],
    rsk: ["Car-dependence; minimal rail access",
          "Supply tight but price growth thin"] }),
  "mel-park-orchards":   sb({ bh: 1450, ba: 560, rh: 720, ra: 560, src: MEL_SRC, yr: 2025, tier: "moderate", tf: "long",
    drv: ["Premium leafy large-block character",
          "Rare mid-east green-belt positioning"],
    rsk: ["No rail access",
          "Highly car-dependent lifestyle"] }),
  "mel-doncaster":       sb({ bh: 1120, ba: 520, rh: 620, ra: 520, src: MEL_SRC, yr: 2025, tier: "moderate", tf: "medium",
    drv: ["Westfield + bus SmartBus + freeway access",
          "Asian-migrant family demand steady"],
    rsk: ["No rail access (long-running Doncaster Rail debate)",
          "Apartment pipeline around centre"] }),
  "mel-box-hill":        sb({ bh: 1180, ba: 520, rh: 620, ra: 540, src: MEL_SRC, yr: 2025, tier: "stronger", tf: "medium",
    drv: ["Metro Tunnel station 2025 + major activity centre",
          "Migrant + international-student tenant depth"],
    rsk: ["Apartment supply heavy around centre",
          "Some streets tower-shadow-affected"] }),
  "mel-glen-waverley":   sb({ bh: 1320, ba: 550, rh: 700, ra: 550, src: MEL_SRC, yr: 2025, tier: "stronger", tf: "medium",
    drv: ["Glen Waverley line terminus + activity centre",
          "Strong schools + Monash Uni proximity"],
    rsk: ["Apartment pipeline near centre",
          "Catchment premium already banked"] }),
  "mel-chadstone":       sb({ bh: 1180, ba: 540, rh: 640, ra: 540, src: MEL_SRC, yr: 2025, tier: "moderate", tf: "medium",
    drv: ["Chadstone shopping centre + tram/bus",
          "Family-home depth with mid-east adjacency"],
    rsk: ["Mall-adjacent traffic + noise",
          "Supply mixed; growth steady"] }),
  "mel-oakleigh":        sb({ bh: 1180, ba: 530, rh: 640, ra: 540, src: MEL_SRC, yr: 2025, tier: "stronger", tf: "medium",
    drv: ["Cranbourne/Pakenham line + Greek-quarter retail",
          "Monash Uni + Chadstone proximity"],
    rsk: ["Supply rising at station precinct",
          "Some industrial-adjacent pockets"] }),
  "mel-clayton":         sb({ bh: 1050, ba: 480, rh: 580, ra: 500, src: MEL_SRC, yr: 2025, tier: "stronger", tf: "medium",
    drv: ["Monash Uni + Clayton Hospital + Suburban Rail Loop (SRL) East",
          "SRL Clayton station under construction"],
    rsk: ["Student apartment supply ample",
          "Some pockets light-industrial adjacent"] }),
  /* -- Inner south (premium) ----------------------------------------------- */
  "mel-south-yarra":     sb({ bh: 2150, ba: 620, rh: 950, ra: 620, src: MEL_SRC, yr: 2025, tier: "moderate", tf: "medium",
    drv: ["Chapel St + multiple train + tram routes",
          "Young-professional tenant depth"],
    rsk: ["Apartment oversupply in some towers",
          "Yields weak on newer stock"] }),
  "mel-prahran":         sb({ bh: 1680, ba: 590, rh: 820, ra: 600, src: MEL_SRC, yr: 2025, tier: "moderate", tf: "medium",
    drv: ["Cafe + nightlife + tram corridor",
          "Heritage terrace scarcity premium"],
    rsk: ["Apartment cohort dilutes averages",
          "Limited new land"] }),
  "mel-toorak":          sb({ bh: 5200, ba: 1050, rh: 1950, ra: 820, src: MEL_SRC, yr: 2025, tier: "moderate", tf: "long",
    drv: ["Peak premium address; HNI + international buyer depth",
          "Limited trophy-home supply maintains floor"],
    rsk: ["Yields very thin",
          "Capital-growth tempo slow at premium"] }),
  "mel-armadale":        sb({ bh: 2180, ba: 700, rh: 980, ra: 680, src: MEL_SRC, yr: 2025, tier: "moderate", tf: "long",
    drv: ["High Street retail + Stonnington premium schools",
          "Period-home depth"],
    rsk: ["Tight supply dampens turnover",
          "Premium yields weak"] }),
  "mel-malvern":         sb({ bh: 2380, ba: 680, rh: 1080, ra: 680, src: MEL_SRC, yr: 2025, tier: "moderate", tf: "long",
    drv: ["Glenferrie Rd retail + tram + train",
          "Premium school catchments"],
    rsk: ["Mature growth; thin upside",
          "Entry very high"] }),
  "mel-caulfield":       sb({ bh: 1380, ba: 560, rh: 720, ra: 570, src: MEL_SRC, yr: 2025, tier: "moderate", tf: "medium",
    drv: ["Monash Uni Caulfield + racecourse + trains",
          "Student + young-pro tenant depth"],
    rsk: ["Apartment supply around station",
          "Mixed housing stock"] }),
  "mel-st-kilda":        sb({ bh: 1380, ba: 560, rh: 720, ra: 560, src: MEL_SRC, yr: 2025, tier: "moderate", tf: "medium",
    drv: ["Beachfront + Acland St + tram",
          "Young-professional + short-stay tenant mix"],
    rsk: ["Apartment cohort drags average",
          "Seasonal demand variability"] }),
  "mel-elwood":          sb({ bh: 1850, ba: 700, rh: 980, ra: 640, src: MEL_SRC, yr: 2025, tier: "moderate", tf: "medium",
    drv: ["Beachside + village character + tram",
          "Low-rise heritage stock scarcity"],
    rsk: ["Flood pocket risk in Elster Ck catchment",
          "Tight supply limits turnover"] }),
  "mel-port-melbourne":  sb({ bh: 1680, ba: 680, rh: 880, ra: 680, src: MEL_SRC, yr: 2025, tier: "moderate", tf: "medium",
    drv: ["Bay Street + light rail + Fishermans Bend rezoning long-term",
          "Proximity to CBD + Crown"],
    rsk: ["Apartment tower cohort pricing soft",
          "Industrial-interface legacy on some streets"] }),
  "mel-albert-park":     sb({ bh: 2180, ba: 760, rh: 1180, ra: 700, src: MEL_SRC, yr: 2025, tier: "moderate", tf: "long",
    drv: ["Heritage cottage precinct + lake + beach",
          "Very tight supply"],
    rsk: ["Entry price high; yields thin",
          "F1 disruption annually"] }),
  "mel-middle-park":     sb({ bh: 2480, ba: 820, rh: 1280, ra: 720, src: MEL_SRC, yr: 2025, tier: "moderate", tf: "long",
    drv: ["Heritage Victorian cottage precinct + beach",
          "Tightly-held HNI address"],
    rsk: ["Very tight stock",
          "Yields well below 2%"] }),
  "mel-bentleigh":       sb({ bh: 1380, ba: 560, rh: 720, ra: 570, src: MEL_SRC, yr: 2025, tier: "moderate", tf: "medium",
    drv: ["Frankston line + Centre Rd retail + schools",
          "Level-crossing removal complete"],
    rsk: ["Supply tight; growth steady not explosive",
          "Some apartments on arterial"] }),
  /* -- Bayside ------------------------------------------------------------- */
  "mel-brighton":        sb({ bh: 3050, ba: 920, rh: 1420, ra: 760, src: MEL_SRC, yr: 2025, tier: "moderate", tf: "long",
    drv: ["Bayside premium address; schools + beach",
          "Trophy heritage supply constrained"],
    rsk: ["Very high entry price",
          "Yields weak"] }),
  "mel-hampton":         sb({ bh: 2180, ba: 720, rh: 1080, ra: 660, src: MEL_SRC, yr: 2025, tier: "moderate", tf: "medium",
    drv: ["Hampton St retail + beach + train",
          "Strong schools + family stock"],
    rsk: ["Tight supply dampens turnover",
          "Premium yields thin"] }),
  "mel-sandringham":     sb({ bh: 1980, ba: 680, rh: 950, ra: 640, src: MEL_SRC, yr: 2025, tier: "moderate", tf: "medium",
    drv: ["Terminus station + Bay Road retail + beach",
          "Leafy streets + schools"],
    rsk: ["Mature growth; fresh supply thin",
          "Commute length to CBD"] }),
  "mel-mentone":         sb({ bh: 1380, ba: 540, rh: 680, ra: 540, src: MEL_SRC, yr: 2025, tier: "moderate", tf: "medium",
    drv: ["Frankston line + Parkdale/beach + schools",
          "Level-crossing removal complete"],
    rsk: ["Apartment supply along station",
          "Mature growth steady"] }),
  "mel-cheltenham":      sb({ bh: 1080, ba: 500, rh: 580, ra: 500, src: MEL_SRC, yr: 2025, tier: "stronger", tf: "medium",
    drv: ["SRL South East terminus planned + Southland mall",
          "Level-crossing removals complete"],
    rsk: ["SRL timeline risk",
          "Some industrial-edge pockets"] }),
  "mel-moorabbin":       sb({ bh: 1020, ba: 480, rh: 560, ra: 480, src: MEL_SRC, yr: 2025, tier: "moderate", tf: "medium",
    drv: ["Frankston line + airport + arterial access",
          "Employment cluster diversification"],
    rsk: ["Industrial-edge character on some streets",
          "Airport noise"] }),
  "mel-black-rock":      sb({ bh: 1680, ba: 580, rh: 820, ra: 560, src: MEL_SRC, yr: 2025, tier: "moderate", tf: "long",
    drv: ["Beach + leafy family-home character",
          "Premium bayside pocket"],
    rsk: ["No rail access",
          "Car-dependent lifestyle"] }),
  "mel-beaumaris":       sb({ bh: 1540, ba: 560, rh: 750, ra: 540, src: MEL_SRC, yr: 2025, tier: "moderate", tf: "medium",
    drv: ["Beach + mid-century modernist stock",
          "Strong schools + mature family feel"],
    rsk: ["No rail access",
          "Mature supply; growth thin"] }),
  /* -- Inner west ---------------------------------------------------------- */
  "mel-footscray":       sb({ bh: 980, ba: 460, rh: 540, ra: 480, src: MEL_SRC, yr: 2025, tier: "stronger", tf: "medium",
    drv: ["Regional rail hub + Metro Tunnel benefit + VU campus",
          "Gentrifying retail corridor"],
    rsk: ["Patchy-safety perception still lingers",
          "Industrial-adjacent streets"] }),
  "mel-west-footscray":  sb({ bh: 880, ba: 430, rh: 500, ra: 450, src: MEL_SRC, yr: 2025, tier: "stronger", tf: "medium",
    drv: ["Sunbury line + level-crossing removal works",
          "Young-family gentrification spilling from Footscray"],
    rsk: ["Some streets freight-rail adjacent",
          "Industrial-edge history"] }),
  "mel-yarraville":      sb({ bh: 1180, ba: 530, rh: 620, ra: 540, src: MEL_SRC, yr: 2025, tier: "moderate", tf: "medium",
    drv: ["Village + Sun Theatre + Werribee line",
          "Tight Edwardian cottage supply"],
    rsk: ["West Gate truck route nearby",
          "Heritage overlay costs rising"] }),
  "mel-seddon":          sb({ bh: 1220, ba: 540, rh: 640, ra: 560, src: MEL_SRC, yr: 2025, tier: "moderate", tf: "medium",
    drv: ["Village retail + Werribee line + tight stock",
          "Family-gentrification momentum"],
    rsk: ["Very tight supply limits turnover",
          "Some streets arterial-adjacent"] }),
  "mel-newport":         sb({ bh: 1080, ba: 500, rh: 580, ra: 510, src: MEL_SRC, yr: 2025, tier: "moderate", tf: "medium",
    drv: ["Werribee line + bay-adjacent + village retail",
          "Family-home character"],
    rsk: ["Arterial truck traffic on periphery",
          "Flood overlay in some pockets"] }),
  "mel-williamstown":    sb({ bh: 1520, ba: 580, rh: 780, ra: 580, src: MEL_SRC, yr: 2025, tier: "moderate", tf: "medium",
    drv: ["Bayside heritage village + Williamstown line",
          "Scarce historic cottage stock"],
    rsk: ["Commute via single rail line",
          "Industrial-harbour history on some streets"] }),
  "mel-altona":          sb({ bh: 980, ba: 470, rh: 540, ra: 480, src: MEL_SRC, yr: 2025, tier: "moderate", tf: "medium",
    drv: ["Beach + Altona loop line + village retail",
          "Family-home value vs inner west"],
    rsk: ["Some industrial-adjacent pockets",
          "Refinery proximity + air-quality concerns"] }),
  /* -- West growth --------------------------------------------------------- */
  "mel-sunshine":        sb({ bh: 760, ba: 420, rh: 460, ra: 450, src: MEL_SRC, yr: 2025, tier: "stronger", tf: "medium",
    drv: ["Regional rail hub + proposed airport rail + MTIA activity centre",
          "Young family + migrant depth"],
    rsk: ["Public-space upgrade still underway",
          "Mixed-character streets"] }),
  "mel-werribee":        sb({ bh: 620, ba: 380, rh: 430, ra: 420, src: MEL_SRC, yr: 2025, tier: "moderate", tf: "medium",
    drv: ["Werribee line terminus + university + growth corridor",
          "Large-block family-home depth"],
    rsk: ["Commute 50+ min peak",
          "Some streets industrial-adjacent"] }),
  "mel-point-cook":      sb({ bh: 760, ba: 420, rh: 500, ra: 460, src: MEL_SRC, yr: 2025, tier: "moderate", tf: "medium",
    drv: ["Bay-adjacent master-planned community",
          "Family-home scale + school options"],
    rsk: ["Single-road access capacity strained",
          "Bus-dependent transit"] }),
  "mel-tarneit":         sb({ bh: 680, ba: 400, rh: 460, ra: 430, src: MEL_SRC, yr: 2025, tier: "stronger", tf: "medium",
    drv: ["Regional Rail Link + 6 new primary schools 2024–25",
          "Migrant-family demand + low entry"],
    rsk: ["Infrastructure still catching up to growth",
          "Road capacity stressed peak"] }),
  "mel-truganina":       sb({ bh: 680, ba: 400, rh: 470, ra: 430, src: MEL_SRC, yr: 2025, tier: "stronger", tf: "medium",
    drv: ["Major master-planned estates + new schools 2024–26",
          "Regional rail + freeway upgrades underway"],
    rsk: ["Infrastructure maturity 3–5y out",
          "Large cohort of investor-owned stock"] }),
  "mel-hoppers-crossing":sb({ bh: 640, ba: 380, rh: 440, ra: 430, src: MEL_SRC, yr: 2025, tier: "moderate", tf: "medium",
    drv: ["Werribee line + Werribee Plaza mall",
          "Affordable family-home stock"],
    rsk: ["Commute length + rail crowding",
          "Mature stock needing upgrades"] }),
  "mel-laverton":        sb({ bh: 680, ba: 400, rh: 450, ra: 430, src: MEL_SRC, yr: 2025, tier: "moderate", tf: "long",
    drv: ["Werribee line + Defence + logistics employment",
          "Close to Point Cook + Altona"],
    rsk: ["Mixed industrial-residential character",
          "Some streets freeway-noise"] }),
  "mel-st-albans":       sb({ bh: 720, ba: 410, rh: 460, ra: 440, src: MEL_SRC, yr: 2025, tier: "moderate", tf: "medium",
    drv: ["Sunbury line + level-crossing removal",
          "Migrant-family demand + VU Footscray Park"],
    rsk: ["Some streets arterial-adjacent",
          "Mature stock requiring upgrades"] }),
  "mel-deer-park":       sb({ bh: 720, ba: 410, rh: 460, ra: 440, src: MEL_SRC, yr: 2025, tier: "moderate", tf: "medium",
    drv: ["Sunbury line extension + new station",
          "Affordable family-home entry"],
    rsk: ["Industrial-adjacent history",
          "Road capacity strained"] }),
  "mel-melton":          sb({ bh: 560, ba: 360, rh: 400, ra: 420, src: MEL_SRC, yr: 2025, tier: "moderate", tf: "long",
    drv: ["Fastest-growing LGA in Victoria + new schools",
          "Future metro rail electrification advocacy"],
    rsk: ["Commute 60+ min peak",
          "Infrastructure catching up slowly"] }),
  "mel-wyndham-vale":    sb({ bh: 600, ba: 370, rh: 430, ra: 420, src: MEL_SRC, yr: 2025, tier: "moderate", tf: "long",
    drv: ["Regional Rail Link + new schools",
          "Wyndham growth area"],
    rsk: ["Single-line transit crowding",
          "Infra still catching up"] }),
  "mel-manor-lakes":     sb({ bh: 620, ba: 380, rh: 430, ra: 420, src: MEL_SRC, yr: 2025, tier: "moderate", tf: "long",
    drv: ["Master-planned estate + new schools + retail 2025",
          "Affordable entry with lifestyle amenity"],
    rsk: ["Commute length",
          "Estate road capacity maturing"] }),
  "mel-rockbank":        sb({ bh: 640, ba: 390, rh: 430, ra: 420, src: MEL_SRC, yr: 2025, tier: "stronger", tf: "long",
    drv: ["New Rockbank station 2024 + Caroline Springs spill",
          "Large parcels + master-planned growth"],
    rsk: ["Long-term infrastructure spend-down",
          "Resale depth thin pre-maturity"] }),
  "mel-plumpton":        sb({ bh: 620, ba: 380, rh: 430, ra: 420, src: MEL_SRC, yr: 2025, tier: "stronger", tf: "long",
    drv: ["Melton growth belt + new schools 2025",
          "Lower entry than Truganina equivalent"],
    rsk: ["Rail access still via Caroline Springs only",
          "Infrastructure timeline dependent"] }),
  "mel-aintree":         sb({ bh: 620, ba: 380, rh: 430, ra: 420, src: MEL_SRC, yr: 2025, tier: "moderate", tf: "long",
    drv: ["Melton growth belt + new estate amenity",
          "Family-home scale"],
    rsk: ["Car-dependent at present",
          "Rail station upgrades pending"] }),
  "mel-williams-landing":sb({ bh: 780, ba: 420, rh: 490, ra: 460, src: MEL_SRC, yr: 2025, tier: "moderate", tf: "medium",
    drv: ["Rail station + town centre + freeway access",
          "Master-planned premium-growth estate"],
    rsk: ["Tight estate character limits variation",
          "Price growth slowing vs earlier cycle"] }),
  "mel-diggers-rest":    sb({ bh: 580, ba: 370, rh: 420, ra: 420, src: MEL_SRC, yr: 2025, tier: "stronger", tf: "long",
    drv: ["Sunbury line + outer growth release",
          "New estates + low entry"],
    rsk: ["Remote commute length",
          "Social infra still early-stage"] }),
  "mel-sydenham":        sb({ bh: 720, ba: 410, rh: 460, ra: 440, src: MEL_SRC, yr: 2025, tier: "moderate", tf: "medium",
    drv: ["Sunbury line + Watergardens mall",
          "Family-home affordability"],
    rsk: ["Mature stock + road capacity constraints",
          "Some arterial-adjacent streets"] }),
  "mel-taylors-lakes":   sb({ bh: 760, ba: 410, rh: 470, ra: 440, src: MEL_SRC, yr: 2025, tier: "moderate", tf: "medium",
    drv: ["Watergardens proximity + leafy streets",
          "Larger blocks than Sydenham"],
    rsk: ["Car-dependent at lake pockets",
          "Mature supply; growth steady"] }),
  /* -- North growth --------------------------------------------------------- */
  "mel-broadmeadows":    sb({ bh: 560, ba: 340, rh: 400, ra: 410, src: MEL_SRC, yr: 2025, tier: "moderate", tf: "long",
    drv: ["Craigieburn line + Hume activity centre",
          "Major redevelopment plan in pipeline"],
    rsk: ["Safety perception still lags",
          "Slow redevelopment cadence"] }),
  "mel-meadow-heights":  sb({ bh: 560, ba: 340, rh: 400, ra: 410, src: MEL_SRC, yr: 2025, tier: "moderate", tf: "long",
    drv: ["Hume growth + affordable entry",
          "Craigieburn line proximity"],
    rsk: ["Mature stock + some streets industrial",
          "Growth tied to Hume activity-centre plan"] }),
  "mel-craigieburn":     sb({ bh: 640, ba: 380, rh: 450, ra: 420, src: MEL_SRC, yr: 2025, tier: "moderate", tf: "medium",
    drv: ["Craigieburn line terminus + Hume growth",
          "Central station precinct upgrades"],
    rsk: ["Commute length + rail crowding",
          "Infrastructure catch-up cycle"] }),
  "mel-roxburgh-park":   sb({ bh: 560, ba: 340, rh: 400, ra: 410, src: MEL_SRC, yr: 2025, tier: "moderate", tf: "long",
    drv: ["Craigieburn line + affordable entry",
          "Family-size housing stock"],
    rsk: ["Mature stock needing upgrades",
          "Commute + rail crowding"] }),
  "mel-mickleham":       sb({ bh: 520, ba: 340, rh: 400, ra: 410, src: MEL_SRC, yr: 2025, tier: "stronger", tf: "long",
    drv: ["Major Hume release belt + new schools pipeline",
          "Merrifield activity centre growth"],
    rsk: ["Road capacity + bus-only transit",
          "Social infra 3–5y from maturity"] }),
  "mel-kalkallo":        sb({ bh: 520, ba: 340, rh: 400, ra: 410, src: MEL_SRC, yr: 2025, tier: "stronger", tf: "long",
    drv: ["Next-wave Hume growth release (post-Mickleham)",
          "Freeway + rail upgrades mid-term"],
    rsk: ["Very early-stage infrastructure",
          "Resale depth thin"] }),
  "mel-epping":          sb({ bh: 680, ba: 400, rh: 460, ra: 430, src: MEL_SRC, yr: 2025, tier: "moderate", tf: "medium",
    drv: ["Mernda line + Pacific Epping + Northern Hospital",
          "Family-home depth"],
    rsk: ["Some streets arterial-adjacent",
          "Supply growing around hospital"] }),
  "mel-mernda":          sb({ bh: 640, ba: 390, rh: 440, ra: 420, src: MEL_SRC, yr: 2025, tier: "moderate", tf: "medium",
    drv: ["Mernda line terminus + growth-corridor family stock",
          "New schools opened 2023–24"],
    rsk: ["Commute length to CBD",
          "Estate-style amenity maturing"] }),
  "mel-doreen":          sb({ bh: 700, ba: 400, rh: 470, ra: 430, src: MEL_SRC, yr: 2025, tier: "moderate", tf: "medium",
    drv: ["Laurimar + Doreen town centres + family demand",
          "Mernda line + Plenty Valley"],
    rsk: ["Road access thin",
          "No direct rail station in Doreen itself"] }),
  "mel-wollert":         sb({ bh: 620, ba: 380, rh: 430, ra: 420, src: MEL_SRC, yr: 2025, tier: "stronger", tf: "long",
    drv: ["Whittlesea growth belt + Lockerbie SRL-adjacent",
          "Planned rail extension + new schools 2025–28"],
    rsk: ["Bus-only transit today",
          "Infrastructure timeline risk"] }),
  "mel-south-morang":    sb({ bh: 720, ba: 410, rh: 460, ra: 430, src: MEL_SRC, yr: 2025, tier: "moderate", tf: "medium",
    drv: ["Mernda line + Plenty Valley + retail cluster",
          "Family-home stock"],
    rsk: ["Mature growth; fresh supply tight",
          "Some arterial-noise streets"] }),
  "mel-donnybrook":      sb({ bh: 580, ba: 370, rh: 420, ra: 420, src: MEL_SRC, yr: 2025, tier: "stronger", tf: "long",
    drv: ["Hume outer-growth release; freeway-side entry",
          "Future rail electrification considered"],
    rsk: ["Commute currently long",
          "Social infra early-stage"] }),
  "mel-lalor":           sb({ bh: 680, ba: 400, rh: 440, ra: 420, src: MEL_SRC, yr: 2025, tier: "moderate", tf: "medium",
    drv: ["Mernda line + mature retail spine",
          "Affordable family-home stock"],
    rsk: ["Aging housing stock",
          "Arterial-noise on High Street"] }),
  "mel-thomastown":      sb({ bh: 720, ba: 410, rh: 460, ra: 430, src: MEL_SRC, yr: 2025, tier: "moderate", tf: "medium",
    drv: ["Mernda line + industrial employment",
          "Affordable family entry"],
    rsk: ["Industrial-adjacent pockets",
          "Mature stock needing upgrades"] }),
  /* -- South-east growth --------------------------------------------------- */
  "mel-dandenong":       sb({ bh: 680, ba: 400, rh: 460, ra: 430, src: MEL_SRC, yr: 2025, tier: "stronger", tf: "medium",
    drv: ["Major activity centre + Cranbourne/Pakenham line",
          "Revitalisation investment + Chisholm TAFE"],
    rsk: ["Safety perception still lags CBD average",
          "Industrial edge on some streets"] }),
  "mel-springvale":      sb({ bh: 780, ba: 420, rh: 490, ra: 450, src: MEL_SRC, yr: 2025, tier: "moderate", tf: "medium",
    drv: ["Pakenham line + Vietnamese-Asian retail spine",
          "Activity centre + family depth"],
    rsk: ["Arterial-noise on Springvale Road",
          "Mature growth; fresh supply tight"] }),
  "mel-noble-park":      sb({ bh: 720, ba: 410, rh: 460, ra: 440, src: MEL_SRC, yr: 2025, tier: "moderate", tf: "medium",
    drv: ["Pakenham line + family housing stock",
          "Affordable vs Dandenong + Springvale"],
    rsk: ["Some streets arterial-adjacent",
          "Mature stock requiring upgrades"] }),
  "mel-frankston":       sb({ bh: 820, ba: 440, rh: 520, ra: 470, src: MEL_SRC, yr: 2025, tier: "moderate", tf: "medium",
    drv: ["Frankston line terminus + beach + activity centre",
          "Peninsula gateway + revitalisation investment"],
    rsk: ["Mixed-character streets",
          "Commute length to CBD"] }),
  "mel-cranbourne":      sb({ bh: 660, ba: 390, rh: 440, ra: 430, src: MEL_SRC, yr: 2025, tier: "moderate", tf: "medium",
    drv: ["Pakenham line + Casey activity centre",
          "Affordable family-home entry"],
    rsk: ["Commute length peak",
          "Road capacity strained"] }),
  "mel-cranbourne-east": sb({ bh: 680, ba: 390, rh: 450, ra: 430, src: MEL_SRC, yr: 2025, tier: "moderate", tf: "medium",
    drv: ["Estate-style family growth + schools",
          "Casey + Pakenham adjacency"],
    rsk: ["Bus-only last-mile transit",
          "Road capacity maturing"] }),
  "mel-cranbourne-west": sb({ bh: 640, ba: 380, rh: 440, ra: 420, src: MEL_SRC, yr: 2025, tier: "moderate", tf: "medium",
    drv: ["Growth estate + affordable entry",
          "Pakenham line proximity via feeder"],
    rsk: ["Transit access via bus",
          "Amenity still maturing"] }),
  "mel-berwick":         sb({ bh: 820, ba: 430, rh: 520, ra: 450, src: MEL_SRC, yr: 2025, tier: "moderate", tf: "medium",
    drv: ["Federation Uni + Casey Hospital + schools",
          "Pakenham line + freeway access"],
    rsk: ["Sprawl character; car-dependent pockets",
          "Mature stock needing upgrades"] }),
  "mel-officer":         sb({ bh: 680, ba: 400, rh: 450, ra: 430, src: MEL_SRC, yr: 2025, tier: "moderate", tf: "long",
    drv: ["Pakenham line + new growth estates",
          "Outer Cardinia release"],
    rsk: ["Infrastructure catch-up cycle",
          "Commute length"] }),
  "mel-clyde-north":     sb({ bh: 700, ba: 400, rh: 460, ra: 430, src: MEL_SRC, yr: 2025, tier: "stronger", tf: "long",
    drv: ["Major Casey release belt + new schools + new rail station discussions",
          "Large family-home depth + master-planned estates"],
    rsk: ["Rail station timeline uncertain",
          "Road capacity still stressed"] }),
  "mel-pakenham":        sb({ bh: 660, ba: 390, rh: 450, ra: 430, src: MEL_SRC, yr: 2025, tier: "moderate", tf: "medium",
    drv: ["Pakenham line terminus + Cardinia growth",
          "Lifestyle + family-home depth"],
    rsk: ["Commute length",
          "Mature stock upgrades lagging"] }),
};

/* -------------------------------------------------------------------------- */
/*  BLR — seed (2026-05). Sub-set of the 50-suburb BLR catalogue, focused on  */
/*  commonly-compared suburbs. Same units as HYD: ₹ Lakh for buy, ₹ k/mo for  */
/*  rent.                                                                     */
/* -------------------------------------------------------------------------- */

const BLR_SRC = "MagicBricks + 99acres + Knight Frank India 2025";

export const BLR_SUBURB_PRICING: Record<string, PricingBankEntry> = {
  "blr-indiranagar":     sb({ bh: 350, ba: 180, rh: 95, ra: 55, src: BLR_SRC, yr: 2025, tier: "moderate", tf: "long",
    drv: ["100ft Road retail + bar density anchors tenant demand",
          "Purple Line metro (Indiranagar station) keeps premium intact"],
    rsk: ["Apartment supply tightening; villa stock essentially capped",
          "Weekend traffic + parking pressure deters family upgraders"] }),
  "blr-koramangala":     sb({ bh: 320, ba: 170, rh: 90, ra: 52, src: BLR_SRC, yr: 2025, tier: "moderate", tf: "short",
    drv: ["Startup hub + Forum Mall anchor demand pool",
          "10–20 min to most south/east IT campuses"],
    rsk: ["Inner-ring rush snarls evening commute",
          "Bachelor turnover reduces yield stability"] }),
  "blr-whitefield":      sb({ bh: 150, ba: 100, rh: 60, ra: 35, src: BLR_SRC, yr: 2025, tier: "stronger", tf: "medium",
    drv: ["ITPL + EPIP + Brookfields cluster employment density",
          "Whitefield metro (Purple east end) operational since 2023"],
    rsk: ["Long evening commute to central BLR",
          "Apartment supply remains heavy 2024–25"] }),
  "blr-electronic-city": sb({ bh: 130, ba: 80,  rh: 50, ra: 28, src: BLR_SRC, yr: 2025, tier: "moderate", tf: "medium",
    drv: ["Anchored by Infosys/Wipro/Biocon/HCL campuses",
          "Hosur Road elevated expressway (NICE) shortens central commute"],
    rsk: ["Limited walkable retail/social density vs central pockets",
          "RR Nagar / Bommanahalli congestion persists"] }),
  "blr-hsr-layout":      sb({ bh: 220, ba: 130, rh: 70, ra: 42, src: BLR_SRC, yr: 2025, tier: "stronger", tf: "medium",
    drv: ["Sector-grid layout + walkable retail strips",
          "10–15 min to Outer Ring Road tech-park belt"],
    rsk: ["Apartment medians up 18% YoY 2024–25 — affordability stretched",
          "School zoning concentrated; new families compete for private seats"] }),
  "blr-sarjapur":        sb({ bh: 150, ba: 95,  rh: 55, ra: 33, src: BLR_SRC, yr: 2025, tier: "stronger", tf: "long",
    drv: ["ORR-east tech-corridor expansion + ELC schools cluster",
          "Sarjapur–Marathahalli ORR widening underway"],
    rsk: ["Road infrastructure lags supply",
          "School fees inflated by Wipro / Inventure / Greenwood demand"] }),
  "blr-bellandur":       sb({ bh: 160, ba: 110, rh: 60, ra: 38, src: BLR_SRC, yr: 2025, tier: "moderate", tf: "medium",
    drv: ["Embassy Tech Village + RGA Tech Park within 3 km",
          "ORR + Sarjapur Road frontage on west side"],
    rsk: ["Bellandur Lake + ORR lake-froth optics persist",
          "Apartment supply heavy; older stock yield-only"] }),
  "blr-marathahalli":    sb({ bh: 140, ba: 95,  rh: 55, ra: 35, src: BLR_SRC, yr: 2025, tier: "moderate", tf: "medium",
    drv: ["ORR retail bridge + 100ft Road retail spine",
          "Bridge link to Whitefield + ITPL"],
    rsk: ["Junction-area traffic remains punishing",
          "Older stock dominates; villa replacement lags"] }),
  "blr-mahadevapura":    sb({ bh: 160, ba: 105, rh: 58, ra: 36, src: BLR_SRC, yr: 2025, tier: "stronger", tf: "medium",
    drv: ["RMZ Ecospace + ITPL adjacency",
          "Purple Line extension + ORR easy access"],
    rsk: ["Industrial-zone pockets remain mixed-use",
          "Rentals rising 15% YoY 2024–25 stretch budgets"] }),
  "blr-jayanagar":       sb({ bh: 280, ba: 150, rh: 65, ra: 38, src: BLR_SRC, yr: 2025, tier: "moderate", tf: "long",
    drv: ["Tree-lined arterial grid + 4th Block retail",
          "Green/Yellow Line metro envelopes south corridor"],
    rsk: ["Heritage zoning constraints; new supply minimal",
          "Family-home stock turnover slow"] }),
  "blr-jp-nagar":        sb({ bh: 220, ba: 130, rh: 60, ra: 36, src: BLR_SRC, yr: 2025, tier: "moderate", tf: "medium",
    drv: ["Adjacent to Jayanagar + Bannerghatta Road",
          "Phase 7+ infill + metro access via Yellow Line"],
    rsk: ["Bannerghatta Road infrastructure perennially stressed",
          "Apartment quality bifurcated — 2010s vs 2020s vintage"] }),
  "blr-banashankari":    sb({ bh: 220, ba: 130, rh: 55, ra: 32, src: BLR_SRC, yr: 2025, tier: "moderate", tf: "long",
    drv: ["Green Line metro + family-home density",
          "South corridor connectivity to Mysore Road"],
    rsk: ["Older infrastructure on Phase 1/2 streets",
          "Apartment supply concentrated in Phase 6 new builds"] }),
  "blr-malleshwaram":    sb({ bh: 280, ba: 150, rh: 65, ra: 38, src: BLR_SRC, yr: 2025, tier: "moderate", tf: "long",
    drv: ["Sampige Road retail + Green Line metro",
          "Heritage neighbourhood; tenant pool stable"],
    rsk: ["Heritage zoning caps redevelopment",
          "Apartment supply minimal; villa stock dominant"] }),
  "blr-rajajinagar":     sb({ bh: 260, ba: 140, rh: 60, ra: 36, src: BLR_SRC, yr: 2025, tier: "moderate", tf: "medium",
    drv: ["Magadi Road metro + ORR-west connectivity",
          "Established residential character"],
    rsk: ["Ageing stock; major upgrades concentrated in pockets",
          "Commercial encroachment on residential blocks"] }),
  "blr-cooke-town":      sb({ bh: 280, ba: 145, rh: 70, ra: 40, src: BLR_SRC, yr: 2025, tier: "weaker", tf: "long",
    drv: ["Heritage bungalow + colonial-era retail spine",
          "Walkability + cafe culture intact"],
    rsk: ["Heritage zoning caps supply expansion",
          "Yields compressed by absent rental scaling"] }),
  "blr-frazer-town":     sb({ bh: 240, ba: 135, rh: 65, ra: 38, src: BLR_SRC, yr: 2025, tier: "weaker", tf: "long",
    drv: ["Mosque-adjacent retail + Sunday morning brunch belt",
          "Cantonment-area tenant pool"],
    rsk: ["Building-stock quality varies block-to-block",
          "Limited new-launch volume"] }),
  "blr-richmond-town":   sb({ bh: 290, ba: 150, rh: 70, ra: 40, src: BLR_SRC, yr: 2025, tier: "moderate", tf: "long",
    drv: ["Bishop Cotton schools belt + heritage bungalow stock",
          "Cantonment connectivity + Brigade Road walk"],
    rsk: ["Heritage caps supply; rare new-build releases",
          "School fees lock out non-legacy buyers"] }),
  "blr-sadashivanagar":  sb({ bh: 450, ba: 220, rh: 110, ra: 60, src: BLR_SRC, yr: 2025, tier: "weaker", tf: "long",
    drv: ["BLR's most-prestigious residential pocket; large-lot villa stock",
          "Diplomatic + corporate-CEO tenant pool"],
    rsk: ["Buy-side liquidity thin; few sales per year",
          "Yield compressed; investor entry weak"] }),
  "blr-vasanth-nagar":   sb({ bh: 240, ba: 135, rh: 65, ra: 38, src: BLR_SRC, yr: 2025, tier: "weaker", tf: "long",
    drv: ["Cantonment-edge heritage bungalow stock",
          "Walk to Cubbon Park + MG Road retail"],
    rsk: ["Building stock ageing; modernisation slow",
          "Limited apartment alternatives"] }),
  "blr-domlur":          sb({ bh: 230, ba: 140, rh: 65, ra: 40, src: BLR_SRC, yr: 2025, tier: "moderate", tf: "medium",
    drv: ["Indiranagar adjacency + flyover access to ORR",
          "Embassy Golf Links Tech Park 2 km"],
    rsk: ["Junction-area traffic worsens with ORR feeds",
          "Older 1990s apartment stock pushes refurbishment costs"] }),
  "blr-hebbal":          sb({ bh: 180, ba: 110, rh: 55, ra: 32, src: BLR_SRC, yr: 2025, tier: "stronger", tf: "medium",
    drv: ["Manyata Tech Park anchor + airport connectivity (NH 44)",
          "Lake-edge premium parcels"],
    rsk: ["Flyover-area traffic chronic at peak",
          "Apartment supply heavy in 2024–25 launches"] }),
  "blr-yelahanka":       sb({ bh: 130, ba: 80,  rh: 45, ra: 26, src: BLR_SRC, yr: 2025, tier: "moderate", tf: "long",
    drv: ["Airport corridor + KIA expressway access",
          "Schools cluster (Stonehill / Mallya Aditi / Canadian)"],
    rsk: ["Mature retail/social density still developing",
          "Long commute to south/central tech corridors"] }),
  "blr-mg-road":         sb({ ba: 200, ra: 65, src: BLR_SRC, yr: 2025, tier: "weaker", tf: "long",
    drv: ["CBD retail + commercial spine; mostly apartments",
          "Cantonment metro + walkable to Brigade Road"],
    rsk: ["Apartment-only supply; family-floorplate scarce",
          "Commercial encroachment dominates ground floors"] }),
  "blr-brigade-road":    sb({ ba: 180, ra: 60, src: BLR_SRC, yr: 2025, tier: "weaker", tf: "long",
    drv: ["Pedestrian retail spine; high foot-traffic adjacency",
          "Cantonment-zone connectivity"],
    rsk: ["Apartment-only mix; mostly investor-held",
          "Noise + retail hours limit family appeal"] }),
  "blr-kr-puram":        sb({ bh: 140, ba: 90,  rh: 50, ra: 30, src: BLR_SRC, yr: 2025, tier: "moderate", tf: "medium",
    drv: ["Whitefield-adjacent + Purple Line metro extension",
          "ORR/east entry corridor"],
    rsk: ["Industrial-fringe character in pockets",
          "Junction traffic at peak"] }),
};

/* -------------------------------------------------------------------------- */
/*  SYD — seed (2026-05). Sub-set of the 50-suburb SYD catalogue. Units mirror */
/*  MEL: A$ thousands for buy, A$/wk for rent.                                */
/* -------------------------------------------------------------------------- */

const SYD_SRC = "Domain + realestate.com.au + CoreLogic 2025";

export const SYD_SUBURB_PRICING: Record<string, PricingBankEntry> = {
  "syd-cbd":             sb({ ba: 880, ra: 750, src: SYD_SRC, yr: 2025, tier: "weaker", tf: "long",
    drv: ["CBD train/light-rail interchange depth",
          "Apartment-only mix simplifies tenant comparison"],
    rsk: ["Family floor-plates rare; mostly studio/1-2BR",
          "Service-charge strata fees high"] }),
  "syd-surry-hills":     sb({ bh: 2400, ba: 1080, rh: 1100, ra: 750, src: SYD_SRC, yr: 2025, tier: "moderate", tf: "medium",
    drv: ["Walkable to CBD + Central Station",
          "Inner-city village character (Crown St + Bourke St)"],
    rsk: ["Victorian terrace stock — family layout limits",
          "Premium pricing locks out yield-focused investors"] }),
  "syd-darlinghurst":    sb({ ba: 850, ra: 620, src: SYD_SRC, yr: 2025, tier: "moderate", tf: "medium",
    drv: ["Oxford St retail + Kings Cross adjacency",
          "Walk to CBD + Hyde Park"],
    rsk: ["Apartment-dominant mix; family-floor stock thin",
          "Late-night district noise on Oxford St strip"] }),
  "syd-pyrmont":         sb({ ba: 1080, ra: 800, src: SYD_SRC, yr: 2025, tier: "moderate", tf: "medium",
    drv: ["Light rail + harbourside walking spine",
          "Star casino + tech-precinct adjacency"],
    rsk: ["Apartment-only; strata costs high",
          "Construction noise persistent over development cycle"] }),
  "syd-glebe":           sb({ bh: 2200, ba: 980, rh: 1050, ra: 700, src: SYD_SRC, yr: 2025, tier: "moderate", tf: "medium",
    drv: ["Glebe Point + harbourside walking spine",
          "Walk to USYD + light rail"],
    rsk: ["Heritage caps supply; family-floor terraces rare",
          "Renter turnover high (USYD demand)"] }),
  "syd-newtown":         sb({ bh: 2050, ba: 920, rh: 980, ra: 680, src: SYD_SRC, yr: 2025, tier: "stronger", tf: "medium",
    drv: ["King St strip + USYD adjacency",
          "Inner-west character; rental demand strong"],
    rsk: ["Inner-city noise on the strip",
          "Old terrace stock — family floor-plates limited"] }),
  "syd-marrickville":    sb({ bh: 1850, ba: 850, rh: 880, ra: 620, src: SYD_SRC, yr: 2025, tier: "stronger", tf: "medium",
    drv: ["Inner-west growth corridor + Sydenham metro",
          "Cafe/maker scene + family-friendly arterials"],
    rsk: ["Aircraft noise corridor on east side",
          "Older stock requires upgrade for modern living"] }),
  "syd-leichhardt":      sb({ bh: 2280, ba: 980, rh: 1080, ra: 720, src: SYD_SRC, yr: 2025, tier: "moderate", tf: "long",
    drv: ["Norton St Italian retail spine + heritage terrace stock",
          "Inner-west character + 8 km to CBD"],
    rsk: ["Heritage caps supply expansion",
          "Public school zone competition tight"] }),
  "syd-balmain":         sb({ bh: 2680, ba: 1180, rh: 1280, ra: 820, src: SYD_SRC, yr: 2025, tier: "moderate", tf: "long",
    drv: ["Peninsula geography + harbour aspect on west",
          "Darling Street retail + ferry to CBD"],
    rsk: ["Commute corridor stressed at peak",
          "Heritage caps supply; turnover slow"] }),
  "syd-bondi":           sb({ bh: 4100, ba: 1450, rh: 1500, ra: 880, src: SYD_SRC, yr: 2025, tier: "moderate", tf: "long",
    drv: ["Beach-front lifestyle anchor; iconic global brand",
          "Bondi Junction interchange + Eastern Suburbs train"],
    rsk: ["Premium pricing locks out yield-focused investors",
          "Summer crowding + parking pressure"] }),
  "syd-coogee":          sb({ bh: 3450, ba: 1280, rh: 1320, ra: 820, src: SYD_SRC, yr: 2025, tier: "moderate", tf: "long",
    drv: ["Beach-front + UNSW adjacency",
          "Walking-spine retail along Coogee Bay Road"],
    rsk: ["Buses-only public transport (no train)",
          "Premium pricing; yield compressed"] }),
  "syd-randwick":        sb({ bh: 3050, ba: 1180, rh: 1180, ra: 780, src: SYD_SRC, yr: 2025, tier: "moderate", tf: "medium",
    drv: ["UNSW + Prince of Wales Hospital tenant pool",
          "Light rail extension + arterial bus network"],
    rsk: ["Older apartment stock dominates rental supply",
          "Family floor-plates concentrated in expensive pockets"] }),
  "syd-paddington":      sb({ bh: 3050, ba: 1380, rh: 1420, ra: 850, src: SYD_SRC, yr: 2025, tier: "moderate", tf: "long",
    drv: ["Heritage terrace stock + Oxford St retail",
          "Walk to CBD + Centennial Park adjacency"],
    rsk: ["Heritage caps supply; new-build rare",
          "Premium pricing — family mass-market upgraders priced out"] }),
  "syd-double-bay":      sb({ bh: 5500, ba: 1850, rh: 2000, ra: 1100, src: SYD_SRC, yr: 2025, tier: "weaker", tf: "long",
    drv: ["Eastern Suburbs prestige + harbour aspects",
          "Boutique retail + cafe density"],
    rsk: ["Buy-side liquidity thin; few sales per year",
          "Yield compressed; investor entry weak"] }),
  "syd-vaucluse":        sb({ bh: 7000, ba: 1980, rh: 2200, ra: 1180, src: SYD_SRC, yr: 2025, tier: "weaker", tf: "long",
    drv: ["Sydney's apex residential pocket; harbour-front estates",
          "Diplomatic + corporate-CEO tenant pool"],
    rsk: ["Buy-side liquidity extremely thin",
          "Yields essentially zero — buyer pool capital, not return"] }),
  "syd-woollahra":       sb({ bh: 4200, ba: 1380, rh: 1450, ra: 880, src: SYD_SRC, yr: 2025, tier: "weaker", tf: "long",
    drv: ["Heritage Victorian terrace stock + Queen St retail",
          "Walk to Centennial Park + Paddington adjacency"],
    rsk: ["Heritage caps supply",
          "Premium pricing locks out family upgraders"] }),
  "syd-mosman":          sb({ bh: 5500, ba: 1450, rh: 1700, ra: 880, src: SYD_SRC, yr: 2025, tier: "weaker", tf: "long",
    drv: ["Lower North Shore prestige + harbour ferries",
          "Family-friendly walking-spine retail (Military Rd)"],
    rsk: ["Premium pricing; yield compressed",
          "Buy-side liquidity slow"] }),
  "syd-north-sydney":    sb({ bh: 3500, ba: 1180, rh: 1280, ra: 780, src: SYD_SRC, yr: 2025, tier: "moderate", tf: "medium",
    drv: ["Selective-school cluster (NSBoys/Girls) + train hub",
          "5-min commute to CBD via train"],
    rsk: ["Apartment-dominant supply; family floor-plates scarce",
          "Construction zone over Metro+ Towers cycle"] }),
  "syd-chatswood":       sb({ bh: 3050, ba: 850, rh: 1280, ra: 650, src: SYD_SRC, yr: 2025, tier: "moderate", tf: "medium",
    drv: ["Train hub + retail centre (Westfield + The Concourse)",
          "Asian-cuisine retail density unmatched"],
    rsk: ["Apartment supply heavy in central tower belt",
          "Family-house stock concentrated in west pockets"] }),
  "syd-killara":         sb({ bh: 4100, ba: 1180, rh: 1500, ra: 850, src: SYD_SRC, yr: 2025, tier: "moderate", tf: "long",
    drv: ["Killara High catchment (selective)",
          "North Shore train + leafy Federation home stock"],
    rsk: ["Buy-side liquidity slow",
          "Apartment alternatives limited"] }),
  "syd-gordon":          sb({ bh: 3500, ba: 980, rh: 1280, ra: 720, src: SYD_SRC, yr: 2025, tier: "moderate", tf: "long",
    drv: ["North Shore train + Ravenswood/Knox/Pymble Ladies' adjacency",
          "Family-home depth on grid"],
    rsk: ["Apartment supply concentrated near station",
          "Premium pricing on family blocks"] }),
  "syd-lindfield":       sb({ bh: 4100, ba: 1080, rh: 1450, ra: 780, src: SYD_SRC, yr: 2025, tier: "moderate", tf: "long",
    drv: ["North Shore train + private-school feeder pool",
          "Federation-home depth on grid"],
    rsk: ["Buy-side thin; few annual sales",
          "Family-floor apartment stock dominant near station"] }),
  "syd-wahroonga":       sb({ bh: 4050, ba: 1080, rh: 1280, ra: 720, src: SYD_SRC, yr: 2025, tier: "moderate", tf: "long",
    drv: ["Knox Grammar + Abbotsleigh + train hub",
          "Federation-home + leafy aspect"],
    rsk: ["Long commute to CBD",
          "Premium pricing; buy-side liquidity slow"] }),
  "syd-castle-hill":     sb({ bh: 1980, ba: 880, rh: 900, ra: 600, src: SYD_SRC, yr: 2025, tier: "stronger", tf: "medium",
    drv: ["Metro Northwest + Castle Towers retail",
          "Family-home depth + Hills District schools"],
    rsk: ["Long commute via Metro to CBD (~45 min)",
          "Apartment supply growing near station"] }),
  "syd-baulkham-hills":  sb({ bh: 1700, ba: 780, rh: 850, ra: 580, src: SYD_SRC, yr: 2025, tier: "stronger", tf: "medium",
    drv: ["Selective school (Baulkham Hills High) + Hills District depth",
          "Metro Northwest connectivity"],
    rsk: ["Commute distance + traffic on M2",
          "Apartment supply growing"] }),
  "syd-hornsby":         sb({ bh: 1850, ba: 720, rh: 900, ra: 580, src: SYD_SRC, yr: 2025, tier: "stronger", tf: "medium",
    drv: ["Train hub + Hornsby Westfield + selective Hornsby Girls",
          "Family-home depth on Asquith / Waitara"],
    rsk: ["Long commute to CBD (~45 min)",
          "Apartment supply growing near station"] }),
  "syd-strathfield":     sb({ bh: 3050, ba: 850, rh: 1100, ra: 620, src: SYD_SRC, yr: 2025, tier: "moderate", tf: "medium",
    drv: ["Train hub (8 min to CBD) + private-school cluster",
          "Asian-cuisine retail density"],
    rsk: ["Apartment supply heavy near station",
          "Family-house stock pricing matches eastern suburbs"] }),
  "syd-burwood":         sb({ bh: 2480, ba: 780, rh: 950, ra: 600, src: SYD_SRC, yr: 2025, tier: "moderate", tf: "medium",
    drv: ["Burwood Westfield + train hub + Sydney Boys/Girls feeder pool",
          "Inner-west connectivity"],
    rsk: ["Apartment supply heavy near station",
          "Construction noise active on tower cycle"] }),
  "syd-parramatta":      sb({ bh: 1450, ba: 700, rh: 750, ra: 580, src: SYD_SRC, yr: 2025, tier: "stronger", tf: "medium",
    drv: ["Greater Sydney's second CBD + James Ruse selective adjacency",
          "Train hub + Parramatta Light Rail"],
    rsk: ["Apartment supply heavy in central tower belt",
          "Family-house stock concentrated in north/west pockets"] }),
  "syd-liverpool":       sb({ bh: 1050, ba: 570, rh: 620, ra: 470, src: SYD_SRC, yr: 2025, tier: "stronger", tf: "long",
    drv: ["Western Sydney Airport adjacency + train hub",
          "First-home-buyer affordability + stamp-duty exemptions"],
    rsk: ["Long commute to CBD",
          "Apartment supply growing aggressively"] }),
  "syd-penrith":         sb({ bh: 950, ba: 540, rh: 580, ra: 450, src: SYD_SRC, yr: 2025, tier: "moderate", tf: "long",
    drv: ["Western Sydney growth corridor + Nepean River lifestyle",
          "First-home-buyer affordability"],
    rsk: ["Long commute to CBD (~60 min train)",
          "Apartment supply still developing"] }),
  "syd-blacktown":       sb({ bh: 1050, ba: 580, rh: 620, ra: 470, src: SYD_SRC, yr: 2025, tier: "moderate", tf: "medium",
    drv: ["Train hub + Westpoint Blacktown retail",
          "First-home-buyer + investor depth"],
    rsk: ["Long commute to CBD",
          "Apartment supply concentrated near station"] }),
  "syd-cronulla":        sb({ bh: 2620, ba: 1080, rh: 1100, ra: 720, src: SYD_SRC, yr: 2025, tier: "moderate", tf: "long",
    drv: ["Beach-front + train terminus + Sutherland Shire identity",
          "Walkable retail along Cronulla Mall"],
    rsk: ["Long commute to CBD (~60 min)",
          "Premium pricing on beachside blocks"] }),
  "syd-manly":           sb({ bh: 4500, ba: 1280, rh: 1500, ra: 850, src: SYD_SRC, yr: 2025, tier: "moderate", tf: "long",
    drv: ["Beach-front + ferry to Circular Quay",
          "Strong tenant pool from CBD-commuting professionals"],
    rsk: ["Premium pricing on beachside blocks",
          "Ferry-only fast commute (B-Line bus secondary)"] }),
};

/* -------------------------------------------------------------------------- */
/*  Lookup + audit helpers                                                    */
/* -------------------------------------------------------------------------- */

/**
 * Apply a monthly override (if any) on top of a bank entry. Only provided
 * `priceData` fields are overwritten — investorEvidence is NEVER touched
 * by the override, so qualitative evidence stays hand-curated even after
 * an auto-refresh.
 *
 * Silent no-op when no override exists for this id.
 */
function applyOverride(id: string, entry: PricingBankEntry): PricingBankEntry {
  const o = OVERRIDE.entries[id];
  if (!o) return entry;
  return {
    ...entry,
    priceData: {
      ...entry.priceData,
      ...(o.buyHouseMedian != null ? { buyHouseMedian: o.buyHouseMedian } : {}),
      ...(o.buyApartmentMedian != null ? { buyApartmentMedian: o.buyApartmentMedian } : {}),
      ...(o.rentHouseMedian != null ? { rentHouseMedian: o.rentHouseMedian } : {}),
      ...(o.rentApartmentMedian != null ? { rentApartmentMedian: o.rentApartmentMedian } : {}),
      ...(o.source ? { source: o.source } : {}),
      ...(o.year != null ? { year: o.year } : {}),
    },
  };
}

/** Return bank entry for a given suburb id (override-applied), or null if none. */
export function getPricingFor(id: string): PricingBankEntry | null {
  const raw =
    HYD_SUBURB_PRICING[id] ??
    MEL_SUBURB_PRICING[id] ??
    BLR_SUBURB_PRICING[id] ??
    SYD_SUBURB_PRICING[id] ??
    null;
  return raw ? applyOverride(id, raw) : null;
}

export interface PricingAudit {
  total: number;
  covered: string[];
  missing: string[];
}

/**
 * Walk a verdicts array and return which ids resolve to a researched
 * pricing bank entry vs fall through to the band fallback. Used by the
 * build script + a dev-only console sanity check.
 */
export function auditSuburbPricingCoverage(
  verdicts: ReadonlyArray<{ id: string; priceData?: unknown }>
): PricingAudit {
  const covered: string[] = [];
  const missing: string[] = [];
  for (const v of verdicts) {
    const hasInline = v.priceData != null;
    const hasBank = getPricingFor(v.id) != null;
    if (hasInline || hasBank) covered.push(v.id);
    else missing.push(v.id);
  }
  return { total: verdicts.length, covered, missing };
}
